import PropTypes from 'prop-types'
import ReactECharts from 'echarts-for-react'
import { createRadarChartOption } from '../../../utils/chartUtils'

/**
 * Infrastructure Radar Chart
 *
 * Shows infrastructure readiness as a radar chart with critical gaps highlighted
 */
function InfrastructureRadar({ data, title, narrative, templateText, gaps }) {
  if (!data || data.length === 0) {
    return (
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-600 text-sm">
          No infrastructure readiness data available.
        </p>
      </div>
    )
  }

  // Format data for radar chart
  const radarData = data.map(item => ({
    name: item.name,
    value: item.percentage || item.value || 0,
  }))

  const chartOption = createRadarChartOption(radarData, title)

  return (
    <div className="bg-white p-6 rounded-lg border border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>

      {/* Narrative or template text */}
      {(narrative || templateText) && (
        <div className="mb-4">
          <p className="text-gray-700 text-sm leading-relaxed">
            {narrative || templateText}
          </p>
        </div>
      )}

      {/* Chart */}
      <div className="h-96">
        <ReactECharts
          option={chartOption}
          style={{ height: '100%', width: '100%' }}
          opts={{ renderer: 'canvas' }}
          theme="ug-dashboard"
        />
      </div>

      {/* Critical Gaps */}
      {gaps && gaps.length > 0 && (
        <div className="mt-4 border-t border-gray-200 pt-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-2">
            Critical Infrastructure Gaps
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {gaps.map((gap, index) => (
              <div
                key={index}
                className={`flex items-center justify-between px-3 py-2 rounded ${
                  gap.severity === 'high'
                    ? 'bg-red-50 border border-red-200'
                    : gap.severity === 'medium'
                      ? 'bg-yellow-50 border border-yellow-200'
                      : 'bg-blue-50 border border-blue-200'
                }`}
              >
                <span
                  className={`text-sm font-medium ${
                    gap.severity === 'high'
                      ? 'text-red-900'
                      : gap.severity === 'medium'
                        ? 'text-yellow-900'
                        : 'text-blue-900'
                  }`}
                >
                  {gap.name}
                </span>
                <span
                  className={`text-xs font-semibold ${
                    gap.severity === 'high'
                      ? 'text-red-700'
                      : gap.severity === 'medium'
                        ? 'text-yellow-700'
                        : 'text-blue-700'
                  }`}
                >
                  {gap.percentage}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

InfrastructureRadar.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      percentage: PropTypes.number,
      value: PropTypes.number,
    })
  ).isRequired,
  title: PropTypes.string.isRequired,
  narrative: PropTypes.string,
  templateText: PropTypes.string,
  gaps: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      percentage: PropTypes.number.isRequired,
      severity: PropTypes.string.isRequired,
    })
  ),
}

export default InfrastructureRadar
